import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCVStore } from '../store/cvStore'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { FileText, Plus, Upload, Trash2, Edit, Loader2 } from 'lucide-react'

export default function DashboardPage() {
  const navigate = useNavigate()
  const { cvs, loading, fetchCVs, deleteCV, renameCV } = useCVStore()
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameName, setRenameName] = useState('')

  useEffect(() => {
    fetchCVs()
  }, [])

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm('Delete this CV?')) {
      await deleteCV(id)
    }
  }

  const handleRename = async (id: string) => {
    if (renameName.trim()) {
      await renameCV(id, renameName.trim())
    }
    setRenamingId(null)
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My CVs</h1>
          <p className="text-gray-500 mt-1">Manage and optimize your resumes</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/upload')} variant="outline" className="gap-2">
            <Upload className="w-4 h-4" />
            Upload
          </Button>
          <Button onClick={() => { useCVStore.getState().clearCurrent(); navigate('/editor') }} className="gap-2">
            <Plus className="w-4 h-4" />
            New CV
          </Button>
        </div>
      </div>

      {loading && cvs.length === 0 ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : cvs.length === 0 ? (
        <Card className="text-center py-16">
          <CardContent>
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No CVs yet</h2>
            <p className="text-gray-400 mb-6">Create your first CV or upload an existing one</p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => navigate('/upload')} variant="outline" className="gap-2">
                <Upload className="w-4 h-4" />
                Upload CV
              </Button>
              <Button onClick={() => { useCVStore.getState().clearCurrent(); navigate('/editor') }} className="gap-2">
                <Plus className="w-4 h-4" />
                Create CV
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cvs.map((cv) => (
            <Card key={cv.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/editor/${cv.id}`)}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                    {renamingId === cv.id ? (
                      <Input
                        value={renameName}
                        onChange={(e) => setRenameName(e.target.value)}
                        onBlur={() => handleRename(cv.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRename(cv.id); if (e.key === 'Escape') setRenamingId(null) }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 text-sm font-semibold"
                        autoFocus
                      />
                    ) : (
                      <CardTitle className="text-base">{cv.name}</CardTitle>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-2">
                <p className="text-xs text-gray-400">
                  Created {new Date(cv.created_at || cv.createdAt!).toLocaleDateString()}
                  {cv.updated_at !== cv.created_at && ` · Edited ${new Date(cv.updated_at || cv.updatedAt!).toLocaleDateString()}`}
                </p>
              </CardContent>
              <CardFooter className="pt-2 gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setRenamingId(cv.id); setRenameName(cv.name) }}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600"
                  onClick={(e) => handleDelete(cv.id, e)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
